/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React, { createRef } from 'react'
import { withRouter } from 'react-router-dom'
import PropTypes from 'prop-types'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'
import { default as fragments } from '../../fragments'

import {
  Spinner,
  SpanWithData,
  Table,
  ButtonRequestRunInitiate
} from '../'

import AccountDetailsModal from './AccountDetailsModal'

import { RequestsBankAccountCreate } from './RequestsBankAccountCreate'
import { NoAccounts } from './NoAccounts'

import { decodeBase64, getNodeTypeFromBase64Id } from '../../utils'
import { REQUESTTYPE_IDENTITYBANKACCOUNT_CREATE } from '../../constants'

const accountDetailsModalRef = createRef()


class IdentityBankAccounts extends React.Component {
  static propTypes = {
    identityId: PropTypes.string.isRequired
  }

  state = {
    verifyInitiateInProgress: false,
    refetch: false,
    default: false,
    showAccountDetails: null
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside)
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside)
  }

  handleClickOutside = (e) => {
    if (this.state.showAccountDetails !== null && !accountDetailsModalRef.current.contains(e.target)) {
      // click is outside, close modal showing account details
      this.setState({ showAccountDetails: null })
      this.props.data.refetch()
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.data.error && this.props.data.error && !this.state.refetch) {
      this.setState({ refetch: true })
      this.props.data.refetch()
    }

    if (this.state.refetch) {
      this.setState({ refetch: false })
    }
  }

  getVerifiedRow = ({ node: identitybankaccount }) => (
    <tr className='clickable' key={identitybankaccount.id} onClick={() => this.showAccountDetails(identitybankaccount)}>
      <td><SpanWithData forField="BankAccountBankName" searchKey={identitybankaccount.bankaccount.bankName} /></td>
      <td>{identitybankaccount.bankaccount.accountNumber}</td>
      <td className='titlecase'>{identitybankaccount.ownership.toLowerCase()}</td>
      <td>{identitybankaccount.bankaccount.beneficiaryName}</td>
      <td>{identitybankaccount.default ? 'Yes' : 'No'}</td>
    </tr>
  )

  getOtherRow = ({ node: identitybankaccount }) => (
    <tr className='clickable' key={identitybankaccount.id} onClick={() => this.showAccountDetails(identitybankaccount)}>
      <td><SpanWithData forField="BankAccountBankName" searchKey={identitybankaccount.bankaccount.bankName} /></td>
      <td>{identitybankaccount.bankaccount.accountNumber}</td>
      <td className='titlecase'>{identitybankaccount.ownership.toLowerCase()}</td>
      <td>{identitybankaccount.bankaccount.beneficiaryName}</td>
      <td><SpanWithData forField='IdentityBankAccountStatus' searchKey={identitybankaccount.status} /></td>
    </tr>
  )

  goToRequestOrCasePage = (identityId, id) => {
    const nodeType = getNodeTypeFromBase64Id(id)
    if (nodeType === 'RequestRunNode') {
      this.props.history.push(`/os/identity/${identityId}/requests/${id}`)
    } else if (nodeType === 'CaseNode') {
      this.props.history.push(`/os/identity/${identityId}/cases/${id}`)
    }
  }

  showAccountDetails = (identityBankAccount) => (
    this.setState({ showAccountDetails: identityBankAccount })
  )

  render() {
    const { identityId, data } = this.props

    if (data.loading) {
      return <Spinner className="slds-is-relative" />
    }

    if (data.error) {
      return <h1>Oh dear! We have an issue</h1>
    }

    const identityBankAccounts = data.identitybankaccounts.edges

    const verifiedAccounts = identityBankAccounts.filter(({ node: account }) => (
      account.status === 'VERIFIED'
    ))
    const otherAccounts = identityBankAccounts.filter(({ node: account }) => (
      account.status !== 'VERIFIED'
    ))

    return (
      <div className='slds-grid'>
        <div className='slds-size_2-of-3 slds-m-top_large'>
          <div className='u-font-size-large u-font-weight-bold slds-m-bottom_small'>
            Verified accounts
          </div>
          {verifiedAccounts.length === 0
            ? <NoAccounts />
            : <Table
                columns={['Bank name', 'Account no.', 'Ownership', 'Beneficiary', 'Primary']}
                rows={verifiedAccounts.map(this.getVerifiedRow)}
                objectRows={true}
              />
          }
          <div className='u-font-size-large u-font-weight-bold slds-m-top_x-large slds-m-bottom_small'>
            Other accounts
          </div>
          {otherAccounts.length === 0
            ? <NoAccounts />
            : <Table
                columns={['Bank name', 'Account no.', 'Ownership', 'Beneficiary', 'Status']}
                rows={otherAccounts.map(this.getOtherRow)}
                objectRows={true}
              />
          }
        </div>
        <div className='slds-size_1-of-3 slds-p-around_small'>
          <ButtonRequestRunInitiate
            requestTypeId={REQUESTTYPE_IDENTITYBANKACCOUNT_CREATE}
            identityId={identityId}
            buttonType='brand'
            disabled={this.state.verifyInitiateInProgress}
            onInitiateTriggered={() => this.setState({ verifyInitiateInProgress: true })}
            onInitiateDone={requestRunId => (
              this.setState(
                { bankAccountInitiateInProgress: false },
                () => this.goToRequestOrCasePage(identityId, requestRunId)
              )
            )}>

            {this.state.verifyInitiateInProgress
              ? 'Initiating...'
              : 'Add and verify new account'
            }
          </ButtonRequestRunInitiate>
          <RequestsBankAccountCreate
            identityId={identityId}
            onRequestClick={requestRunId => this.goToRequestOrCasePage(identityId, requestRunId)}
          />
          <RequestsBankAccountCreate
            title="Requests cancelled"
            identityId={identityId}
            requestStatus='CLOSED_CANCELLED'
            onRequestClick={id => this.goToRequestOrCasePage(identityId, id)}
            orderBy="-updated_at"
          />
        </div>
        {this.state.showAccountDetails !== null &&
          <AccountDetailsModal
            forwardedRef={accountDetailsModalRef}
            identityBankAccount={this.state.showAccountDetails}
          />
        }
      </div>
    )
  }
}

export const getIdentityBankAccounts = gql`
  query($uuid: UUID!) {
    identitybankaccounts(identity_Id: $uuid) {
      edges {
        node {
          ...IdentityBankAccount
        }
      }
    }
  }
  ${fragments.identityBankAccount}
`

const withData = graphql(getIdentityBankAccounts, {
  options: ({ identityId }) => ({
    variables: {
      uuid: decodeBase64(identityId)
    }
  })
})

export default compose(
  withData,
  withRouter
)(IdentityBankAccounts)



// WEBPACK FOOTER //
// ./src/components/identity-bankaccounts/index.js